import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TradeChartScreen = () => {
  const navigate = useNavigate();
  const { metal } = useParams();
  
  const [volume, setVolume] = useState('1');
  const [lot, setLot] = useState(1);
  const [tradeType, setTradeType] = useState('');
  const [uid, setUid] = useState('');
  const [rate, setRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [high, setHigh] = useState(0);
  const [open, setOpen] = useState(0);
  const [low, setLow] = useState(0);
  const [target, setTarget] = useState('');
  const [stoploss, setStoploss] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [balance, setBalance] = useState(0);
  const [name, setName] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showBalance, setShowBalance] = useState(true);

  const [targetPL, setTargetPL] = useState(0);
  const [targetPLPercentage, setTargetPLPercentage] = useState(0);
  const [stoplossPL, setStoplossPL] = useState(0);
  const [stoplossPLPercentage, setStoplossPLPercentage] = useState(0);

  const orderTypes = [
    { label: 'Market', value: 'market', description: 'Execute at current market price' },
    { label: 'Limit', value: 'limit', description: 'Set your entry price' },
  ];

  useEffect(() => {
    if (!metal) {
      navigate('/');
    }
  }, [metal, navigate]);

  useEffect(() => {
    if (tradeType && volume) {
      const volumeNum = parseFloat(volume) || 0;
      const targetNum = parseFloat(target) || 0;
      const stoplossNum = parseFloat(stoploss) || 0;
      const entryPrice = orderType === 'limit' && limitPrice ? parseFloat(limitPrice) : parseFloat(rate) || 0;
      
      if (tradeType === 'BUY') {
        if (targetNum > 0 && targetNum > entryPrice) {
          const profit = (targetNum - entryPrice) * volumeNum;
          const profitPercentage = ((targetNum - entryPrice) / entryPrice) * 100;
          setTargetPL(profit);
          setTargetPLPercentage(profitPercentage);
        } else {
          setTargetPL(0);
          setTargetPLPercentage(0);
        }
        
        if (stoplossNum > 0 && stoplossNum < entryPrice) {
          const loss = (stoplossNum - entryPrice) * volumeNum;
          const lossPercentage = ((stoplossNum - entryPrice) / entryPrice) * 100;
          setStoplossPL(loss);
          setStoplossPLPercentage(lossPercentage);
        } else {
          setStoplossPL(0);
          setStoplossPLPercentage(0);
        }
      } else if (tradeType === 'SELL') {
        if (targetNum > 0 && targetNum < entryPrice) {
          const profit = (entryPrice - targetNum) * volumeNum;
          const profitPercentage = ((entryPrice - targetNum) / entryPrice) * 100;
          setTargetPL(profit);
          setTargetPLPercentage(profitPercentage);
        } else {
          setTargetPL(0);
          setTargetPLPercentage(0);
        }
        
        if (stoplossNum > 0 && stoplossNum > entryPrice) {
          const loss = (entryPrice - stoplossNum) * volumeNum;
          const lossPercentage = ((entryPrice - stoplossNum) / entryPrice) * 100;
          setStoplossPL(loss);
          setStoplossPLPercentage(lossPercentage);
        } else {
          setStoplossPL(0);
          setStoplossPLPercentage(0);
        }
      }
    } else {
      setTargetPL(0);
      setTargetPLPercentage(0);
      setStoplossPL(0);
      setStoplossPLPercentage(0);
    }
  }, [rate, volume, target, stoploss, tradeType, orderType, limitPrice]);

  useEffect(() => {
    if (!metal) return;

    const interval = setInterval(() => {
      fetch(`https://singlewindowsecurities.com/rest3/metal_api/${metal}`)
        .then((response) => response.json())
        .then((responseJson) => {
          setRate(responseJson.rate);
          setOpen(responseJson.open);
          setHigh(responseJson.high);
          setLow(responseJson.low);
          setLot(responseJson.lots);
        }).catch((error) => {
          console.log(error);
          setLoading(false);
        });
    }, 2000);

    return () => clearInterval(interval);
  }, [metal]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const uid = localStorage.getItem("userid");
        const name = localStorage.getItem("name");
        setName(name || 'User');
        setUid(uid);

        if (uid) {
          const response = await fetch(`https://singlewindowsecurities.com/rest3/wallet_api/${uid}`);
          const responseJson = await response.json();
          setBalance(responseJson.wallet || 0);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => { 
    if(!volume || parseFloat(volume) <= 0) {
      alert("Please enter lot");
      return false;
    }
    
    let target1 = target;
    let stoploss1 = stoploss;
    let limitPrice1 = limitPrice;
    
    if(tradeType === "BUY") {
      if(!target1) target1 = 1000000;
      if(!stoploss1) stoploss1 = 0;
      if(orderType === 'limit' && !limitPrice1) {
        alert("Please enter limit price for limit order");
        return false;
      }
    } 
    if(tradeType === "SELL") {
      if(!target1) target1 = 0;
      if(!stoploss1) stoploss1 = 1000000;
      if(orderType === 'limit' && !limitPrice1) {
        alert("Please enter limit price for limit order");
        return false;
      }
    }
    
    const userid = localStorage.getItem("userid");
    const entryRate = orderType === 'limit' && limitPrice1 ? limitPrice1 : rate;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('userid', userid);
      formData.append('metal', metal);
      formData.append('lot', lot);
      formData.append('rate', entryRate);
      formData.append('lotsize', volume);
      formData.append('btype', tradeType);
      formData.append('market_type', "MCX");
      formData.append('target', target1);
      formData.append('stoploss', stoploss1);
      formData.append('setlimit', limitPrice1);
      formData.append('market', orderType === 'market');
      
      const response = await fetch('https://singlewindowsecurities.com/Rest3/web_app_start_trade/', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });
      
      const responseJson = await response.json();
      console.log(responseJson);
      if(responseJson.status === 1) {
        setTimeout(() => {
          setLoading(false);
          alert(responseJson.msg);
          navigate('/orders');
        }, 6000);
      } else {
        alert(responseJson.msg);
      }
    } catch(error) {
      console.error(error);
      alert('Order submission failed');
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleVolumeChange = (e) => {
    const text = e.target.value;
    const cleanedText = text.replace(/[^0-9.]/g, '');
    const decimalCount = (cleanedText.match(/\./g) || []).length;
    if (decimalCount <= 1) {
      setVolume(cleanedText);
    }
  };

  const getEntryPrice = () => {
    return orderType === 'limit' && limitPrice ? parseFloat(limitPrice) : parseFloat(rate) || 0;
  };

  const getChangePercentage = () => {
    if (open && rate) {
      return ((rate - open) / open * 100).toFixed(2);
    }
    return '0.00';
  };

  const isPositive = rate >= open;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#0a0a0a' }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: '#D4AF37' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: '#D4AF37' }}>Processing Order...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0" style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%)'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(10, 10, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div className="container py-3">
          <div className="d-flex justify-content-between align-items-center">
            <button 
              className="btn"
              onClick={() => navigate(-1)}
              style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '12px',
                width: '36px',
                height: '36px',
                color: '#D4AF37'
              }}
            >
              <i className="fa fa-arrow-left"></i>
            </button>
            
            <div className="text-center">
              <h6 className="mb-0 fw-bold" style={{ color: '#D4AF37' }}>{metal || 'MCX'}</h6>
              <small style={{ color: '#888' }}>Market Open • MCX</small>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <span style={{
                background: 'rgba(212, 175, 55, 0.1)',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                color: '#D4AF37'
              }}>
                #{uid ? uid.slice(-4) : '----'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Price Card */}
      <div className="container mt-3">
        <div style={{
          background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.15), rgba(184, 150, 12, 0.05))',
          borderRadius: '24px',
          padding: '20px',
          border: '1px solid rgba(212, 175, 55, 0.2)'
        }}>
          <div className="text-center">
            <small style={{ color: '#D4AF37', letterSpacing: '1px', fontSize: '11px' }}>CURRENT PRICE</small>
            <h2 className="fw-bold mb-2" style={{ color: '#D4AF37' }}>₹{formatNumber(rate)}</h2>
            <div className="d-flex justify-content-center gap-3">
              <div>
                <small style={{ color: '#888' }}>Open</small>
                <div style={{ color: '#fff', fontWeight: 'bold' }}>₹{formatNumber(open)}</div>
              </div>
              <div>
                <small style={{ color: '#888' }}>Change</small>
                <div style={{ color: isPositive ? '#00C853' : '#FF5252', fontWeight: 'bold' }}>
                  {isPositive ? '↑' : '↓'} {Math.abs(getChangePercentage())}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="container mt-3">
        <div className="row g-3">
          <div className="col-4">
            <div style={{
              background: 'rgba(26, 26, 26, 0.8)',
              borderRadius: '16px',
              padding: '12px',
              textAlign: 'center',
              border: '1px solid rgba(212, 175, 55, 0.1)'
            }}>
              <small style={{ color: '#888', fontSize: '10px' }}>HIGH</small>
              <div style={{ color: '#FF5252', fontWeight: 'bold' }}>₹{formatNumber(high)}</div>
            </div>
          </div>
          <div className="col-4">
            <div style={{
              background: 'rgba(26, 26, 26, 0.8)',
              borderRadius: '16px',
              padding: '12px',
              textAlign: 'center',
              border: '1px solid rgba(212, 175, 55, 0.1)'
            }}>
              <small style={{ color: '#888', fontSize: '10px' }}>LOW</small>
              <div style={{ color: '#00C853', fontWeight: 'bold' }}>₹{formatNumber(low)}</div>
            </div>
          </div>
          <div className="col-4">
            <div style={{
              background: 'rgba(26, 26, 26, 0.8)',
              borderRadius: '16px',
              padding: '12px',
              textAlign: 'center',
              border: '1px solid rgba(212, 175, 55, 0.1)'
            }}>
              <small style={{ color: '#888', fontSize: '10px' }}>LOT SIZE</small>
              <div style={{ color: '#D4AF37', fontWeight: 'bold' }}>{lot} Units</div>
            </div>
          </div>
        </div>
      </div>

      {/* Buy/Sell Buttons */}
      {!tradeType ? (
        <div className="container mt-4">
          <div className="row g-3">
            <div className="col-6">
              <button 
                className="btn w-100 py-4 d-flex flex-column align-items-center"
                onClick={() => setTradeType('SELL')}
                style={{
                  background: 'rgba(255, 82, 82, 0.1)',
                  border: '2px solid rgba(255, 82, 82, 0.3)',
                  borderRadius: '20px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 82, 82, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 82, 82, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa fa-arrow-down" style={{ color: '#FF5252', fontSize: '28px' }}></i>
                <span className="fw-bold mt-2" style={{ color: '#FF5252' }}>SELL</span>
                <small style={{ color: '#888' }}>₹{formatNumber(rate)}</small>
              </button>
            </div>
            <div className="col-6">
              <button 
                className="btn w-100 py-4 d-flex flex-column align-items-center"
                onClick={() => setTradeType('BUY')}
                style={{
                  background: 'rgba(0, 200, 83, 0.1)',
                  border: '2px solid rgba(0, 200, 83, 0.3)',
                  borderRadius: '20px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 200, 83, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 200, 83, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa fa-arrow-up" style={{ color: '#00C853', fontSize: '28px' }}></i>
                <span className="fw-bold mt-2" style={{ color: '#00C853' }}>BUY</span>
                <small style={{ color: '#888' }}>₹{formatNumber(rate)}</small>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mt-4 mb-4">
          <div style={{
            background: 'rgba(20, 20, 20, 0.95)',
            borderRadius: '24px',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            overflow: 'hidden'
          }}>
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <span style={{
                  background: tradeType === 'BUY' ? 'rgba(0, 200, 83, 0.15)' : 'rgba(255, 82, 82, 0.15)',
                  color: tradeType === 'BUY' ? '#00C853' : '#FF5252',
                  padding: '6px 16px',
                  borderRadius: '30px',
                  fontWeight: 'bold'
                }}>
                  {tradeType} {metal}
                </span>
                <button 
                  className="btn"
                  onClick={() => {
                    setTradeType('');
                    setLimitPrice('');
                  }}
                  style={{
                    background: 'rgba(212, 175, 55, 0.1)',
                    borderRadius: '10px',
                    width: '32px',
                    height: '32px',
                    color: '#D4AF37'
                  }}
                >
                  <i className="fa fa-x"></i>
                </button>
              </div>

              {/* Order Type */}
              <div className="mb-3">
                <label className="form-label small" style={{ color: '#D4AF37', letterSpacing: '1px' }}>ORDER TYPE</label>
                <button 
                  className="form-control text-start d-flex justify-content-between align-items-center"
                  onClick={() => setShowOrderModal(true)}
                  style={{
                    background: '#1a1a1a',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '10px 12px'
                  }}
                >
                  <span>{orderTypes.find(t => t.value === orderType)?.label || 'Market'}</span>
                  <i className="fa fa-chevron-down" style={{ color: '#D4AF37' }}></i>
                </button>
              </div>

              {/* Limit Price */}
              {orderType === 'limit' && (
                <div className="mb-3">
                  <label className="form-label small" style={{ color: '#D4AF37' }}>LIMIT PRICE</label>
                  <input 
                    type="number" 
                    className="form-control"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                    placeholder={`Enter limit price (Current: ₹${formatNumber(rate)})`}
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    step="0.01"
                  />
                </div>
              )}

              {/* Volume */}
              <div className="mb-3">
                <label className="form-label small" style={{ color: '#D4AF37' }}>VOLUME (LOTS)</label>
                <div className="input-group">
                  <input 
                    type="number" 
                    className="form-control"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                    placeholder="Enter volume"
                    value={volume}
                    onChange={handleVolumeChange}
                    step="0.1"
                    min="0"
                  />
                  <button 
                    className="btn"
                    onClick={() => {
                      const entryPrice = getEntryPrice();
                      const maxLots = Math.floor(balance / entryPrice);
                      setVolume(maxLots > 0 ? maxLots.toString() : '1');
                    }}
                    style={{
                      background: 'linear-gradient(145deg, #D4AF37, #B8960C)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#000',
                      fontWeight: 'bold'
                    }}
                  >
                    MAX
                  </button>
                </div>
              </div>

              {/* Target & Stop Loss */}
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label small" style={{ color: '#D4AF37' }}>TARGET PRICE</label>
                  <input 
                    type="number" 
                    className="form-control"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                    placeholder={tradeType === 'BUY' ? "Set target above" : "Set target below"}
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small" style={{ color: '#D4AF37' }}>STOP LOSS</label>
                  <input 
                    type="number" 
                    className="form-control"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                    placeholder={tradeType === 'BUY' ? "Set SL below" : "Set SL above"}
                    value={stoploss}
                    onChange={(e) => setStoploss(e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              {/* P&L Calculation */}
              <div style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div className="d-flex justify-content-between align-items-center pb-2 mb-2" style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                  <div>
                    <small style={{ color: '#888' }}>Target P&L</small>
                    <div className="fw-bold" style={{ color: targetPL >= 0 ? '#00C853' : '#FF5252' }}>
                      ₹{Math.abs(targetPL).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-end">
                    <small style={{ color: '#888' }}>Stop Loss P&L</small>
                    <div className="fw-bold" style={{ color: stoplossPL >= 0 ? '#00C853' : '#FF5252' }}>
                      ₹{Math.abs(stoplossPL).toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <small style={{ color: '#888' }}>Entry: ₹{formatNumber(getEntryPrice())}</small>
                  <small style={{ color: '#D4AF37' }}>{orderType === 'limit' ? 'Limit Order' : 'Market Order'}</small>
                </div>
              </div>

              {/* Available Balance */}
              <div className="d-flex justify-content-between align-items-center mb-4 p-3" style={{
                background: 'rgba(212, 175, 55, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.15)'
              }}>
                <div>
                  <small style={{ color: '#888' }}>Available Balance</small>
                  <div className="fw-bold" style={{ color: '#fff' }}>
                    ₹{formatNumber(balance)}
                  </div>
                </div>
                <div className="text-end">
                  <small style={{ color: '#888' }}>Required</small>
                  <div className="fw-bold" style={{ color: '#D4AF37' }}>
                    ₹{formatNumber((getEntryPrice() * (parseFloat(volume) || 0)).toFixed(2))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <button 
                  className="btn flex-fill py-3"
                  onClick={() => {
                    setTradeType('');
                    setLimitPrice('');
                  }}
                  style={{
                    background: 'rgba(212, 175, 55, 0.1)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '16px',
                    color: '#D4AF37',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
                <button 
                  className={`btn flex-fill py-3`}
                  onClick={handleSubmit}
                  style={{
                    background: tradeType === 'BUY' ? 'linear-gradient(145deg, #00C853, #009624)' : 'linear-gradient(145deg, #FF5252, #D32F2F)',
                    border: 'none',
                    borderRadius: '16px',
                    color: '#fff',
                    fontWeight: 'bold'
                  }}
                >
                  {orderType === 'limit' ? 'Place Limit Order' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Type Modal */}
      {showOrderModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{
              background: '#1a1a1a',
              borderRadius: '20px',
              border: '1px solid rgba(212, 175, 55, 0.2)'
            }}>
              <div className="modal-header" style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <h6 className="modal-title fw-bold" style={{ color: '#D4AF37' }}>Select Order Type</h6>
                <button 
                  type="button" 
                  className="btn-close btn-close-white"
                  onClick={() => setShowOrderModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0">
                {orderTypes.map((type) => (
                  <button
                    key={type.value}
                    className="btn w-100 text-start p-3"
                    onClick={() => {
                      setOrderType(type.value);
                      setShowOrderModal(false);
                    }}
                    style={{
                      background: 'transparent',
                      borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${orderType === type.value ? '#D4AF37' : '#555'}`,
                        marginRight: '12px',
                        position: 'relative'
                      }}>
                        {orderType === type.value && (
                          <div style={{
                            width: '10px',
                            height: '10px',
                            background: '#D4AF37',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '3px',
                            left: '3px'
                          }}></div>
                        )}
                      </div>
                      <div>
                        <div className="fw-bold" style={{ color: '#fff' }}>{type.label}</div>
                        <small style={{ color: '#888' }}>{type.description}</small>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeChartScreen;